import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { getRoles, createRole, updateRole, deleteRole } from '../lib/api'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Textarea } from '../components/ui/Textarea'
import { Modal } from '../components/ui/Modal'
import { Badge } from '../components/ui/Badge'
import { Spinner } from '../components/ui/Spinner'
import { PageHeader } from '../components/ui/PageHeader'
import { ConfirmModal, useConfirm } from '../components/ui/ConfirmModal'

const schema = z.object({
  name: z.string().min(2, 'En az 2 karakter'),
  description: z.string().optional(),
})

const ROLE_COLORS = { Admin: 'purple', Editor: 'blue', Viewer: 'gray', Manager: 'indigo' }

export default function Roles() {
  const [modal, setModal] = useState(null)
  const qc = useQueryClient()
  const { confirm, close, state: confirmState } = useConfirm()

  const { data, isLoading } = useQuery({ queryKey: ['roles'], queryFn: getRoles })
  const { register, handleSubmit, reset, formState: { errors } } = useForm({ resolver: zodResolver(schema) })

  function openNew() { reset({ name: '', description: '' }); setModal({ item: null }) }
  function openEdit(item) { reset({ name: item.name, description: item.description ?? '' }); setModal({ item }) }

  const saveMut = useMutation({
    mutationFn: (d) => modal?.item ? updateRole(modal.item.id, d) : createRole(d),
    onSuccess: (result) => {
      qc.setQueryData(['roles'], old =>
        modal?.item
          ? (old ?? []).map(r => r.id === modal.item.id ? result : r)
          : [...(old ?? []), result]
      )
      toast.success('Saved'); setModal(null)
    },
    onError: (e) => toast.error(e.message),
  })
  const deleteMut = useMutation({
    mutationFn: deleteRole,
    onSuccess: (_, deletedId) => {
      qc.setQueryData(['roles'], old => (old ?? []).filter(r => r.id !== deletedId))
      toast.success('Role deleted')
    },
    onError: (e) => toast.error(e.message),
  })

  return (
    <div>
      <PageHeader
        title="Roles"
        description="Manage user roles and permissions"
        action={<Button onClick={openNew}><i className="ti ti-plus" /> New Role</Button>}
      />

      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data?.map(role => (
            <div key={role.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                    <i className="ti ti-shield-check text-indigo-600 text-lg" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900">{role.name}</p>
                      <Badge color={ROLE_COLORS[role.name] ?? 'gray'}>{role.name}</Badge>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{role.description || 'No description'}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <i className="ti ti-users text-gray-400" />
                  <span><strong className="text-gray-700">{role.userCount}</strong> users</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="xs" className="text-gray-400 hover:text-indigo-600" onClick={() => openEdit(role)}>
                    <i className="ti ti-pencil" /> Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    className="text-gray-400 hover:text-red-600"
                    onClick={() => confirm({ title: 'Delete Role', message: `Delete "${role.name}"? This action cannot be undone.`, onConfirm: () => deleteMut.mutate(role.id) })}
                  >
                    <i className="ti ti-trash" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal open={confirmState.open} onClose={close} title={confirmState.title} message={confirmState.message} loading={deleteMut.isPending} onConfirm={() => { confirmState.onConfirm(); close() }} />

      <Modal open={Boolean(modal)} onClose={() => setModal(null)} title={modal?.item ? 'Edit Role' : 'New Role'}>
        <form onSubmit={handleSubmit(d => saveMut.mutate(d))} className="space-y-4">
          <Input label="Role Name" placeholder="Admin, Editor, Viewer..." error={errors.name?.message} {...register('name')} />
          <Textarea label="Description" placeholder="Describe what permissions this role has..." rows={3} {...register('description')} />
          <div className="flex gap-3 pt-2 border-t border-gray-100">
            <Button type="submit" loading={saveMut.isPending} disabled={saveMut.isPending}>Save</Button>
            <Button type="button" variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
